import { Box, Button, Container, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { formatPhone } from '@services/formatService';
import { useSupportContacts, type ContactRole } from './useSupportContacts';

const managedRoles: Array<[ContactRole, string]> = [
  ['market_manager', 'Market Manager'],
  ['account_manager', 'Account Manager'],
  ['sales_rep', 'Sales Representative'],
  ['ar_rep', 'Accounts Receivable'],
];
const selfManagedRoles: Array<[ContactRole, string]> = [
  ['account_manager', 'Customer Success Team'],
  ['ar_rep', 'Accounting Questions'],
];
const linkSx = {
  overflowWrap: 'anywhere',
  width: 'fit-content',
  maxWidth: '100%',
  borderRadius: 0.5,
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
} as const;

export default function SupportPage() {
  const selfManaged = useValetSelfManaged();
  const { assignments, rows, loading, failed, retry } = useSupportContacts();
  const email = selfManaged ? 'customersupport@hopdrive.com' : 'dispatchers@hopdrive.com';
  const team = (selfManaged ? selfManagedRoles : managedRoles).flatMap(([role, label]) => {
    const person = rows.find(row => row.user_id === assignments[role]);
    return person && (person.display_name?.trim() || person.email?.trim() || person.phone?.trim())
      ? [{ role, label, person }]
      : [];
  });

  return (
    <Container maxWidth='lg' component='section' aria-labelledby='support-title' sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography id='support-title' component='h1' variant='h4' gutterBottom>
            Support
          </Typography>
          <Typography color='text.secondary'>
            Find your HopDrive contacts and the details to have ready when you reach out.
          </Typography>
        </Box>

        <Paper
          component='section'
          aria-labelledby='primary-support-title'
          variant='outlined'
          sx={{ p: { xs: 2, sm: 3 } }}
        >
          <Typography id='primary-support-title' component='h2' variant='h6' gutterBottom>
            {selfManaged ? 'HopDrive General Support' : 'Urgent Move Issues'}
          </Typography>
          <Stack spacing={1.5}>
            <Link href={`mailto:${email}`} sx={linkSx}>
              Email {email}
            </Link>
            <Link href='tel:8009137674' sx={linkSx}>
              Call (800) 913-7674
            </Link>
            <Link href='sms:8049448548' sx={linkSx}>
              Text (804) 944-8548
            </Link>
          </Stack>
        </Paper>

        {loading && (
          <Typography role='status' color='text.secondary'>
            Loading your HopDrive team...
          </Typography>
        )}
        {failed && (
          <Box role='alert'>
            <Typography>
              We could not load your assigned contacts. You can still use the support links above.
            </Typography>
            <Button onClick={retry} sx={{ mt: 1 }}>
              Retry contacts
            </Button>
          </Box>
        )}
        {team.length > 0 && (
          <Box component='section' aria-labelledby='support-team-title'>
            <Typography id='support-team-title' component='h2' variant='h6' sx={{ mb: 2 }}>
              Your HopDrive team
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {team.map(({ role, label, person }) => (
                <Paper
                  key={role}
                  component='section'
                  aria-labelledby={`support-${role}`}
                  variant='outlined'
                  sx={{ p: 2, minWidth: 0 }}
                >
                  <Typography id={`support-${role}`} component='h3' variant='subtitle1' fontWeight={600} gutterBottom>
                    {label}
                  </Typography>
                  <Stack spacing={1}>
                    {person.display_name?.trim() && (
                      <Typography sx={{ overflowWrap: 'anywhere' }}>{person.display_name}</Typography>
                    )}
                    {person.email?.trim() && (
                      <Link href={`mailto:${person.email}`} sx={linkSx}>
                        Email {person.email}
                      </Link>
                    )}
                    {person.phone?.trim() && (
                      <Link href={`tel:${person.phone}`} sx={linkSx}>
                        Call {formatPhone(person.phone)}
                      </Link>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Paper variant='outlined' sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography component='h2' variant='h6' gutterBottom>
            Before you contact us
          </Typography>
          <Box component='ul' sx={{ pl: 3, my: 0, '& li': { mb: 1 } }}>
            <li>Your dealership or rooftop.</li>
            <li>The relevant order or move ID or invoice number.</li>
            <li>What happened and what you need help with.</li>
            <li>A screenshot, if it helps explain the issue.</li>
          </Box>
          <Typography sx={{ mt: 2 }}>
            Looking for a walkthrough?{' '}
            <Link component={RouterLink} to='/learning-center' sx={linkSx}>
              Browse Training
            </Link>
          </Typography>
        </Paper>
      </Stack>
    </Container>
  );
}
